<template>
  <div class="azcon-home">
    <!-- Hero Slider -->
    <azcon-slider class="azcon-home__slider" :advertisements="advertisements" :loading="sliderLoading"/>

    <!-- Main Grid -->
    <main class="grid">
      <!-- Left column -->
      <div class="grid-left">
        <!-- Trainings with Tabs -->
        <section class="card card-trainings">
          <!-- Skeleton -->
          <azcon-catalog-skeleton v-if="catalogLoading"/>

          <!-- Loaded -->
          <template v-else>
            <azcon-tab-bar :tabs="catalogTabs" :value="activeTab" @input="onTabChange"/>

            <!-- Tab Content -->
            <transition name="tab-slide" mode="out-in">
              <!-- Atama Tab -->
              <div v-if="activeTab === 'assignments'" key="assignments">
                <div v-if="homeAssignments.length" class="training-grid">
                  <azcon-training-card v-for="(item, idx) in homeAssignments"
                                       :key="'assignment_' + idx"
                                       :training="item"
                                       :route="{ name: 'user.assignment-detail', params: { id: item.id } }"
                  />
                </div>
                <div v-else class="empty-state">
                  <icon-bell class="empty-state__icon" :size="48"/>
                  <p class="empty-state__text">{{ $t('general.no_data') || 'Henüz atama bulunmuyor' }}</p>
                </div>
                <router-link v-if="homeAssignments.length" class="see-all" :to="{ name: 'user.my-assignment', query: { tab: 'with_expired', val: 'false' } }">
                  {{ $t('general.show_all') || 'Tümünü Gör' }}
                  <icon-chevron-right :size="16"/>
                </router-link>
              </div>

              <!-- Katalog Tab -->
              <div v-else-if="activeTab === 'catalog'" key="catalog">
                <!-- Kurum Kategorileri -->
                <div v-if="institutionCategories.length" class="category-section category-section--no-border">
                  <azcon-chip-swiper :items="categoryChipItems" item-key="id">
                    <template #default="{ item: cat }">
                      <button :class="['category-chip', { 'category-chip--active': selectedCategory === cat.id }]"
                              @click="selectCategory(cat.id)"
                      >
                        {{ cat.name }}
                      </button>
                    </template>
                  </azcon-chip-swiper>

                  <template v-if="selectedCategory !== null">
                    <div v-if="categoryLoading" class="training-grid">
                      <azcon-training-card v-for="idx in 6" :key="'cat_skel_' + idx" :loading="true"/>
                    </div>
                    <template v-else>
                      <div v-if="currentCategoryTrainings.length" class="training-grid">
                        <azcon-training-card v-for="(training, idx) in currentCategoryTrainings"
                                             :key="'cat_training_' + idx"
                                             :training="training"
                                             :route="categoryTrainingRoute(training)"
                        />
                      </div>
                      <div v-else class="empty-state">
                        <icon-folder-plus class="empty-state__icon" :size="48"/>
                        <p class="empty-state__text">{{ $t('general.no_catalog_training') || 'Eğitim bulunmuyor' }}</p>
                      </div>
                      <router-link v-if="currentCategoryTrainings.length" class="see-all" :to="categoryRoute">
                        {{ $t('general.show_all') || 'Tümünü Gör' }}
                        <icon-chevron-right :size="16"/>
                      </router-link>
                    </template>
                  </template>
                </div>
              </div>

            <!-- Entegrasyonlar Tab -->
            <div v-else-if="activeTab === 'integrations'" key="integrations">
              <template v-if="hasProviders">
                <!-- Provider Chips -->
                <azcon-chip-swiper :items="activeProviders" item-key="key">
                  <template #default="{ item: provider }">
                    <button :class="['provider-chip', 'provider-chip--' + provider.key, { 'provider-chip--active': selectedProvider === provider.key }]"
                            @click="selectProvider(provider.key)"
                    >
                      <span v-if="provider.logo" class="provider-chip__logo">
                        <img :src="provider.logo" alt="Go Fluent"/>
                      </span>
                      <span v-else class="provider-chip__badge">{{ provider.badge }}</span>
                      <span class="provider-chip__label">{{ provider.label }}</span>
                    </button>
                  </template>
                </azcon-chip-swiper>

                <!-- Provider Trainings -->
                <transition name="tab-slide" mode="out-in">
                  <div :key="selectedProvider">
                    <div v-if="isGoFluentProvider" class="gofluent-panel">
                      <div class="gofluent-panel__brand">
                        <img :src="goFluentLogoSrc" alt="Go Fluent" class="gofluent-panel__logo"/>
                      </div>
                      <p class="gofluent-panel__desc">
                        {{$t('general.go_fluent_exp')}}
                      </p>
                      <a
                        :href="goFluentUrl"
                        class="gofluent-panel__cta"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {{$t('general.go_to_gofluent')}}
                        <icon-chevron-right :size="16"/>
                      </a>
                    </div>

                    <template v-else>
                      <div class="training-grid">
                        <azcon-provider-card v-for="(item, idx) in currentProviderList"
                                             :key="'provider_' + idx"
                                             :training="item"
                                             :loading="providerLoading"
                                             :provider="selectedProvider"
                        />
                        <div v-if="!providerLoading && !currentProviderList.length" class="empty-state">
                          <icon-browser class="empty-state__icon" :size="48"/>
                          <p class="empty-state__text">{{ $t('general.no_data') || 'Henüz içerik bulunmuyor' }}</p>
                        </div>
                      </div>
                      <router-link v-if="currentProviderList.length && !providerLoading"
                                   class="see-all"
                                   :to="{ name: 'user.providers-trainings', params: { provider: selectedProvider } }">
                        {{ $t('general.see_all') || 'Tümünü Gör' }}
                        <icon-chevron-right :size="16"/>
                      </router-link>
                    </template>
                  </div>
                </transition>
              </template>

              <!-- Entegrasyon yoksa -->
              <div v-else class="empty-state">
                <icon-connection class="empty-state__icon" :size="48"/>
                <p class="empty-state__text">{{ $t('general.no_integration') || 'Aktif entegrasyon bulunmuyor' }}</p>
                <p class="empty-state__sub">{{ $t('general.no_integration_desc') || 'Entegrasyonlar aktif edildiğinde burada görüntülenecektir' }}</p>
              </div>
            </div>
          </transition>
          </template>
        </section>

        <!-- Collections Library -->
        <section v-if="collectionList.length" class="card card-collections">
          <div v-if="collectionsLoading" class="skeleton-h2"></div>
          <h2 v-else> {{$t('general.library')}}</h2>
          <azcon-chip-swiper v-if="collectionTags.length && !collectionsLoading" :items="collectionTagItems">
            <template #default="{ item: tag }">
              <button :class="['category-chip', { 'category-chip--active': selectedCollectionTag === tag }]"
                      @click="selectCollectionTag(tag)">
                {{ tag === null ? ($t('general.all') || 'Tümü') : tag }}
              </button>
            </template>
          </azcon-chip-swiper>
          <div v-if="collectionsLoading" class="books">
            <azcon-collection-card v-for="idx in 4" :key="'col_skel_' + idx" :loading="true"/>
          </div>
          <azcon-chip-swiper v-else :items="filteredCollections" :gap="12" class="collection-swiper">
            <template #default="{ item: collection }">
              <azcon-collection-card :collection="collection" :loading="false"/>
            </template>
          </azcon-chip-swiper>
          <router-link v-if="!collectionsLoading" class="see-all" :to="{ name: 'user.my-collection', query: { tab: 'all_collections' } }">
            {{ $t('general.show_all') || 'Tümünü Gör' }}
            <icon-chevron-right :size="16"/>
          </router-link>
        </section>

        <!-- Instructors -->
        <section v-if="instructorsLoading || instructors.length" class="card card-instructors">
          <div v-if="instructorsLoading" class="skeleton-h2"></div>
          <h2 v-else>{{ $t("general.internal_trainers") }}</h2>
          <div v-if="instructorsLoading" class="instructor-skeleton-list">
            <div v-for="index in 3" :key="`instructor-skeleton-${index}`"
              class="instructor-card instructor-card--skeleton">
              <span class="instructor-card__photo"></span>
              <span class="instructor-card__content">
                <span class="instructor-card__skeleton-name"></span>
                <span class="instructor-card__skeleton-title"></span>
                <span class="instructor-card__skeleton-count"></span>
              </span>
            </div>
          </div>
          <azcon-chip-swiper v-else :items="instructors" item-key="id" :gap="12" class="instructor-swiper">
            <template #default="{ item: instructor }">
              <button class="instructor-card" type="button" @click="openInstructor(instructor)">
                <img :src="instructor.photo" :alt="instructor.name" class="instructor-card__photo"
                     @error="useInstructorFallbackPhoto"/>
                <span class="instructor-card__content">
                  <strong class="instructor-card__name">{{ instructor.name }}</strong>
                  <span class="instructor-card__title">{{
                    instructor.title || $t("general.internal_instructor")
                  }}</span>
                  <span class="instructor-card__count">
                    {{
                      instructor.trainingCount === null
                        ? $t("general.detail")
                        : $t("general.training_count", {
                            count: instructor.trainingCount,
                          })
                    }}
                  </span>
                </span>
              </button>
            </template>
          </azcon-chip-swiper>
        </section>

        <!-- Stats & Integrations -->
        <section class="card card-stats">
          <azcon-statistics-card :dashboard-item="dashboardItem"/>
          <div v-if="hasProviders" class="integrations">
            <h3>{{ $t('general.integrations') || 'Entegrasyonlar' }}</h3>
            <div class="integration-list">
              <template v-for="provider in activeProviders">
                <a v-if="provider.static"
                   :key="'integration_' + provider.key"
                   :class="'logo-' + provider.key"
                   :href="provider.externalUrl"
                   class="integration-chip"
                   rel="noopener noreferrer"
                   target="_blank"
                >
                  <span v-if="provider.logo" class="logo-badge logo-badge--img">
                    <img :src="provider.logo" alt="Go Fluent"/>
                  </span>
                  <span v-else class="logo-badge">{{ provider.badge }}</span>
                  {{ provider.label }}
                </a>
                <router-link v-else
                             :key="'integration_' + provider.key"
                             :class="'logo-' + provider.key"
                             :to="{ name: 'user.providers-trainings', params: { provider: provider.key } }"
                             class="integration-chip"
                >
                  <span class="logo-badge">{{ provider.badge }}</span>
                  {{ provider.label }}
                </router-link>
              </template>
            </div>
          </div>
        </section>
      </div>

      <!-- Right column -->
      <div class="grid-right">
        <!-- Assignment Calendar -->
        <section class="card card-assignments">
          <div v-if="calendarLoading" class="skeleton-h2"></div>
          <div v-else class="card-title-row">
            <h2> {{$t('general.calender')}} </h2>
            <button
              type="button"
              class="card-title-action"
              :aria-label="calendarEventsPosition === 'right' ? 'Dikey görünüm' : 'Yatay görünüm'"
              @click="toggleCalendarLayout"
            >
              <svg v-if="calendarEventsPosition === 'right'" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <rect x="2.25" y="3" width="13.5" height="12" rx="2" stroke="currentColor" stroke-width="1.5"/>
                <path d="M9 3v12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <rect x="2.25" y="3" width="13.5" height="12" rx="2" stroke="currentColor" stroke-width="1.5"/>
                <path d="M2.5 9h13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <azcon-calendar-card
            :items="calendarItems"
            :loading="calendarLoading"
            :events-position="calendarEventsPosition"
            @item-click="onCalendarItemClick"
          />
        </section>

        <!-- Leaderboard -->
        <section v-if="leaderTableVisible" class="card card-leaderboard">
          <azcon-leaderboard-card :current_user="currentUser"
                                  :dashboard_item="dashboardItem"
                                  :leaderTableVisible="leaderTableVisible"
                                  :leader_board="leaderBoard"
                                  :title="leaderBoardTitle"
                                  :loading="leaderboardLoading"
          />
        </section>
      </div>
    </main>

    <transition name="instructor-modal-fade">
      <div v-if="selectedInstructor"
           class="instructor-modal"
           role="dialog"
           aria-modal="true"
           :aria-labelledby="'instructor-name-' + selectedInstructor.id"
           @click.self="closeInstructor"
      >
        <div class="instructor-modal__dialog">
          <button
            class="instructor-modal__close"
            type="button"
            :aria-label="$t('general.close')"
            @click="closeInstructor"
          >
            &times;
          </button>
          <div class="instructor-modal__profile">
            <img :src="selectedInstructor.photo" :alt="selectedInstructor.name" class="instructor-modal__photo"
                 @error="useInstructorFallbackPhoto"/>
            <div class="instructor-modal__bio">
              <span class="instructor-modal__eyebrow">{{
                $t("general.instructor")
              }}</span>
              <h2 :id="'instructor-name-' + selectedInstructor.id">{{ selectedInstructor.name }}</h2>
              <p class="instructor-modal__title">
                {{
                  selectedInstructor.title ||
                  $t("general.internal_instructor")
                }}
              </p>
              <p>{{
                selectedInstructor.bio ||
                $t("general.instructor_information_not_found")
              }}</p>
            </div>
          </div>
          <div class="instructor-modal__trainings">
            <h3>{{ $t("general.trainings") }}</h3>
            <div v-if="selectedInstructorAssignments.length" class="instructor-training-list">
              <router-link v-for="assignment in selectedInstructorAssignments"
                           :key="assignment.id"
                           :to="assignmentRoute(assignment)"
                           class="instructor-training"
                           @click.native="closeInstructor"
              >
                <div>
                  <strong>{{ assignment.name || assignment.title }}</strong>
                  <span v-if="assignmentDate(assignment)">{{ assignmentDate(assignment) }}</span>
                </div>
                <span class="instructor-training__status">{{ assignmentStatus(assignment) }}</span>
              </router-link>
            </div>
            <p v-else class="instructor-modal__empty">
              {{ $t("general.no_active_assignment_for_instructor") }}
            </p>
          </div>
        </div>
      </div>
    </transition>
    <notice-detail-modal ref="noticeModal" />
  </div>
</template>

<script>
import SecureLS from "secure-ls";
import { dashboardModuleConfigMixin } from "@/mixins/dashboardModuleConfig";
import AzconSlider from "./components/AzconSlider.vue";
import AzconTrainingCard from "./components/AzconTrainingCard.vue";
import AzconCatalogSkeleton from "./components/AzconCatalogSkeleton.vue";
import AzconProviderCard from "./components/AzconProviderCard.vue";
import AzconCalendarCard from "./components/AzconCalendarCard.vue";
import NoticeDetailModal from "@/view/components/modal/NoticeDetailModal.vue";
import AzconCollectionCard from "./components/AzconCollectionCard.vue";
import AzconLeaderboardCard from "./components/AzconLeaderboardCard.vue";
import AzconTabBar from "./components/AzconTabBar.vue";
import AzconChipSwiper from "./components/AzconChipSwiper.vue";
import AzconStatisticsCard from "./components/AzconStatisticsCard.vue";
import IconBook from "@/view/components/icons/IconBook.vue";
import IconCloud from "@/view/components/icons/IconCloud.vue";
import IconFolderPlus from "@/view/components/icons/IconFolderPlus.vue";
import IconBrowser from "@/view/components/icons/IconBrowser.vue";
import IconConnection from "@/view/components/icons/IconConnection.vue";
import IconChevronRight from "@/view/components/icons/IconChevronRight.vue";
import IconBell from "@/view/components/icons/IconBell.vue";
import goFluentLogo from "@/assets/images/goFluent_logo_1.svg";
import noPhoto from "@/assets/images/no-photo.png";
import ApiService from "@/core/services/api.service";
import moment from "moment";

const ls = new SecureLS({ encodingType: 'aes', isCompression: true });

const PROVIDER_MAP = [
  { key: 'linkedin', badge: 'in', label: 'LinkedIn Learning', moduleType: 'linkedin-learning' },
  { key: 'harvard', badge: 'HB', label: 'Harvard Business', moduleType: 'harvard-business' },
  { key: 'udemy', badge: 'U', label: 'Udemy Business', moduleType: 'udemy-business' },
  { key: 'coursera', badge: 'C', label: 'Coursera', moduleType: 'coursera' },
];

const GOFLUENT_PROVIDER = {
  key: 'gofluent',
  badge: 'go',
  label: 'Go Fluent',
  logo: goFluentLogo,
  static: true,
  externalUrl: 'https://azal.gofluent.com',
};

const AEROCLASS_PROVIDER = {
  key: 'aeroclass',
  badge: 'A',
  label: 'Aeroclass',
  static: true,
  externalUrl: '/#/providers-trainings/aeroclass',
};

export default {
  name: "AzconHomePage",
  mixins: [dashboardModuleConfigMixin],
  components: {
    AzconSlider,
    AzconTrainingCard,
    AzconCatalogSkeleton,
    AzconProviderCard,
    AzconCalendarCard,
    NoticeDetailModal,
    AzconCollectionCard,
    AzconLeaderboardCard,
    AzconTabBar,
    AzconChipSwiper,
    AzconStatisticsCard,
    IconBook,
    IconCloud,
    IconFolderPlus,
    IconBrowser,
    IconConnection,
    IconChevronRight,
    IconBell,
  },
  data() {
    return {
      // Component-level loading states
      sliderLoading: true,
      catalogLoading: true,
      collectionsLoading: true,
      calendarLoading: true,
      leaderboardLoading: true,
      statisticsLoading: true,
      instructorsLoading: true,

      leaderTableVisible: true,
      leaderBoardTitle: this.$t('home.leaderboards') || 'Liderler Tablosu',
      activeTab: 'assignments',
      selectedProvider: null,
      providerLoading: false,
      selectedCategory: 0,
      selectedCollectionTag: null,
      categoryLoading: false,
      calendarEventsPosition: "right",
      selfUser: null,
      selectedInstructor: null,
    };
  },
  computed: {
    dashboardItem() {
      return this.$store.getters['dashboardAzcon/dashboardItem'] || {};
    },
    moduleList() {
      return this.$store.getters['dashboardAzcon/moduleList'] || {};
    },
    leaderBoard() {
      return this.$store.getters['dashboardAzcon/leaderBoard'] || {};
    },
    hasStoreData() {
      return this.$store.getters['dashboardAzcon/hasData'];
    },
    instructors() {
      return this.$store.getters['dashboardAzcon/internalInstructors'] || [];
    },
    selectedInstructorAssignments() {
      return Array.isArray(this.selectedInstructor?.trainings)
        ? this.selectedInstructor.trainings
        : [];
    },
    sortedModuleList() {
      return this.moduleList?.dashboard?.filter(item => item.status === 1) || [];
    },
    storeCollections() {
      return this.$store.getters['dashboardAzcon/collections'] || [];
    },
    collections() {
      return this.normalizeCollectionList(this.storeCollections);
    },
    collectionTags() {
      const names = new Set();
      this.collections.forEach(collection => {
        if (collection.tags) {
          try {
            const parsed = typeof collection.tags === 'string' ? JSON.parse(collection.tags) : collection.tags;
            if (Array.isArray(parsed)) {
              parsed.forEach(tag => {
                if (tag && tag.name) names.add(tag.name.trim());
              });
            }
          } catch (e) {
            // parse hatası — atla
          }
        }
      });
      return [...names];
    },
    collectionTagItems() {
      return [null, ...this.collectionTags];
    },
    filteredCollections() {
      if (!this.selectedCollectionTag) return this.collections;
      return this.collections.filter(collection => {
        if (!collection.tags) return false;
        try {
          const parsed = typeof collection.tags === 'string' ? JSON.parse(collection.tags) : collection.tags;
          return Array.isArray(parsed) && parsed.some(tag => tag && tag.name && tag.name.trim() === this.selectedCollectionTag);
        } catch (e) {
          return false;
        }
      });
    },
    collectionList() {
      return this.collectionsLoading ? Array(4).fill({}) : this.filteredCollections;
    },
    rawCalendarItems() {
      return this.$store.getters['dashboardAzcon/calendarItems'] || [];
    },
    calendarItems() {
      return this.rawCalendarItems.map(item => {
        const pivot = item.assigned_users?.[0]?.pivot;
        const isNotice = item._type === 'notice';
        return {
          id: item.id,
          type: isNotice ? 'notice' : (item._route === 'user.education-detail' ? 'training' : 'assignment'),
          route: item._route,
          startDate: moment(isNotice ? item.start_date : (pivot?.created_at || item.start_date), "YYYY-MM-DD").toDate(),
          endDate: moment(isNotice ? item.end_date : (pivot?.created_at || item.end_date), "YYYY-MM-DD").toDate(),
          title: item.name || item.title || "",
          name: item.name || item.title || "",
          text: item.text || "",
          start_date: item.start_date,
          end_date: item.end_date,
        };
      });
    },
    advertisements() {
      return this.dashboardItem.advertisements || [];
    },
    activeProviders() {
      const dynamic = PROVIDER_MAP.filter(p => {
        const module = this.sortedModuleList.find(m => m.type === p.moduleType);
        if (!module) return false;
        if ('user_custom_display' in module && module.user_custom_display === false) return false;
        return true;
      });
      const staticProviders = [];
      if (this.userIntegrationTags.includes(GOFLUENT_PROVIDER.key)) {
        staticProviders.push(GOFLUENT_PROVIDER);
      }
      if (this.userIntegrationTags.includes(AEROCLASS_PROVIDER.key)) {
        staticProviders.push(AEROCLASS_PROVIDER);
      }
      return [...dynamic, ...staticProviders];
    },
    userIntegrationTags() {
      const tags = this.selfUser?.integration_tags;
      if (Array.isArray(tags)) return tags;
      if (typeof tags !== 'string' || !tags.trim()) return [];
      try {
        const parsed = JSON.parse(tags);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return tags.split(',').map(tag => tag.trim()).filter(Boolean);
      }
    },
    isGoFluentProvider() {
      return this.selectedProvider === GOFLUENT_PROVIDER.key;
    },
    goFluentUrl() {
      return GOFLUENT_PROVIDER.externalUrl;
    },
    goFluentLogoSrc() {
      return GOFLUENT_PROVIDER.logo;
    },
    currentUser() {
      return this.authUser || {};
    },
    hasProviders() {
      return this.activeProviders.length > 0;
    },
    catalogTabs() {
      const tabs = [
        {
          key: 'assignments',
          label: this.$t('general.assignment_tab') || 'Atama',
          icon: IconBell,
          badge: true,
        },
        {
          key: 'catalog',
          label: this.$t('general.education_catalog') || 'Katalog',
          icon: IconBook,
        },
      ];
      if (this.hasProviders) {
        tabs.push({
          key: 'integrations',
          label: this.$t('general.integrations') || 'Entegrasyonlar',
          icon: IconCloud,
        });
      }
      return tabs;
    },
    homeAssignments() {
      return this.$store.getters['dashboardAzcon/assignments'] || [];
    },
    providerTrainings() {
      return this.$store.getters['dashboardAzcon/providerTrainings'] || {};
    },
    currentProviderList() {
      if (!this.selectedProvider) return [];
      if (this.providerLoading) return Array(6).fill({});
      const items = this.providerTrainings[this.selectedProvider];
      return Array.isArray(items) ? items.slice(0, 6) : [];
    },
    institutionCategories() {
      return this.$store.getters['dashboardAzcon/trainingCategories'] || [];
    },
    categoryChipItems() {
      return [
        { id: 0, name: this.$t('general.all') || 'Tümü' },
        ...this.institutionCategories,
      ];
    },
    storeCategoryTrainings() {
      return this.$store.getters['dashboardAzcon/categoryTrainings'] || {};
    },
    currentCategoryTrainings() {
      if (this.selectedCategory === null) return [];
      const key = this.selectedCategory;
      const canRegister = this.storeCategoryTrainings[`${key}_can_register`] || [];
      if (canRegister.length >= 6) return canRegister.slice(0, 6);
      const registered = this.storeCategoryTrainings[`${key}_registered`] || [];
      const canRegisterIds = new Set(canRegister.map(t => t.id));
      const extra = registered.filter(t => !canRegisterIds.has(t.id));
      return [...canRegister, ...extra].slice(0, 6);
    },
    categoryRoute() {
      const query = { tab: 'can_register_trainings', tabId: 0 };
      if (this.selectedCategory) {
        query.education = String(this.selectedCategory);
      }
      return { name: 'user.education-category', query };
    },
    isCatalogTrainingVisible() {
      const menuItems = this.moduleList?.menu || [];
      const menuItem = menuItems.find(item => item.type === "education-category");
      return menuItem?.status === 1;
    },
  },
  methods: {
    onCalendarItemClick(item) {
      if (item?.type === 'notice') {
        this.$refs.noticeModal.open(item);
        return;
      }
      if (item?.route && item?.id) {
        this.$router.push({ name: item.route, params: { id: item.id } });
      }
    },
    loadCalendarLayoutPreference() {
      try {
        const v = window.localStorage.getItem("azcon.calendar.eventsPosition");
        if (v === "bottom" || v === "right") {
          this.calendarEventsPosition = v;
        }
      } catch (e) {
        // ignore
      }
    },

    persistCalendarLayoutPreference() {
      try {
        window.localStorage.setItem("azcon.calendar.eventsPosition", this.calendarEventsPosition);
      } catch (e) {
        // ignore
      }
    },

    toggleCalendarLayout() {
      this.calendarEventsPosition = this.calendarEventsPosition === "right" ? "bottom" : "right";
      this.persistCalendarLayoutPreference();
    },

    normalizeCollectionList(source) {
      if (Array.isArray(source)) return source;
      if (source && Array.isArray(source.data)) return source.data;
      return [];
    },

    setAllLoading(value) {
      this.sliderLoading = value;
      this.catalogLoading = value;
      this.collectionsLoading = value;
      this.calendarLoading = value;
      this.leaderboardLoading = value;
      this.statisticsLoading = value;
      this.instructorsLoading = value;
    },

    async initializePage() {
      await this.fetchSelfUser();
      if (this.hasStoreData) {
        // Store'da veri var — hemen göster, arka planda güncelle
        this.setAllLoading(false);
        this.setupDashboardModules();
        this.restoreCategorySelection();
        // noinspection ES6MissingAwait — kasıtlı, arka planda güncellenir
        this.refreshDataInBackground();
      } else {
        await this.fetchData();
      }
    },

    async fetchSelfUser() {
      try {
        const response = await ApiService.get('api/user/self');
        this.selfUser = response?.data || null;
      } catch (error) {
        this.selfUser = null;
        console.error('User self fetch error:', error);
      }
    },

    async fetchData() {
      this.setAllLoading(true);
      try {
        await this.$store.dispatch('dashboardAzcon/fetchDashboardData');
        // Dashboard verisi geldi — slider ve istatistikler hazır
        this.sliderLoading = false;
        this.statisticsLoading = false;
        await this.loadModuleList();
      } catch (error) {
        // Hata durumunda tüm loading'leri kapat
        this.setAllLoading(false);
      }
    },

    async refreshDataInBackground() {
      await this.$store.dispatch('dashboardAzcon/fetchDashboardData');
      await this.loadModuleList();
      // Seçili kategorinin verilerini de arka planda güncelle
      if (this.selectedCategory) {
        await Promise.allSettled([
          this.$store.dispatch('dashboardAzcon/fetchCategoryTrainings', { categoryId: this.selectedCategory, tab: 'can_register' }),
          this.$store.dispatch('dashboardAzcon/fetchCategoryTrainings', { categoryId: this.selectedCategory, tab: 'registered' }),
        ]);
      }
    },

    async loadModuleList() {
      try {
        const result = await this.$store.dispatch('dashboardAzcon/fetchModuleList', this.authUser.company_id);
        if (result?.status) {
          ls.set("dashboardMenuList", result.data);
          this.setupDashboardModules();
          await this.fetchModuleData();
        }
      } catch (error) {
        console.error('Module list fetch error:', error);
        this.setAllLoading(false);
      }
    },

    setupDashboardModules() {
      const dashboard = this.moduleList?.dashboard ?? [];
      const leaderTable = dashboard.find(el => el.type === 'leader_table') || {};
      this.leaderBoardTitle = this.getLocalizedWidgetTitle(leaderTable, this.$t('home.leaderboards'));
      this.leaderTableVisible = leaderTable.status === 1;
    },

    async fetchModuleData() {
      // Her component için ayrı promise — hangisi biterse o component açılır
      const calendarPromise = this.$store.dispatch('dashboardAzcon/fetchCalendarItems', { isCatalogVisible: this.isCatalogTrainingVisible })
        .then(() => { this.calendarLoading = false; })
        .catch(() => { this.calendarLoading = false; });

      const collectionsPromise = this.$store.dispatch('dashboardAzcon/fetchCollections')
        .then(() => { this.collectionsLoading = false; })
        .catch(() => { this.collectionsLoading = false; });

      const instructorsPromise = this.$store.dispatch('dashboardAzcon/fetchInternalInstructors')
        .then(() => { this.instructorsLoading = false; })
        .catch(() => { this.instructorsLoading = false; });

      const catalogPromise = Promise.allSettled([
        this.$store.dispatch('dashboardAzcon/fetchTrainingCategories'),
        this.$store.dispatch('dashboardAzcon/fetchHomeAssignments'),
      ]).then(() => { this.catalogLoading = false; });

      const dashboard = this.moduleList?.dashboard ?? [];
      const leaderTable = dashboard.find(el => el.type === 'leader_table');
      let leaderboardPromise;
      if (leaderTable?.status === 1) {
        leaderboardPromise = this.$store.dispatch('dashboardAzcon/fetchLeaderBoard')
          .then(() => { this.leaderboardLoading = false; })
          .catch(() => { this.leaderboardLoading = false; });
      } else {
        this.leaderboardLoading = false;
      }

      // Provider eğitimlerini de çek (statik Go Fluent hariç)
      this.activeProviders
        .filter(p => !p.static)
        .forEach(p => {
          this.$store.dispatch('dashboardAzcon/fetchProviderTrainings', { provider: p.key, limit: 6 });
        });

      await Promise.allSettled([
        calendarPromise,
        collectionsPromise,
        instructorsPromise,
        catalogPromise,
        leaderboardPromise,
      ].filter(Boolean));

      // Tümü kategorisini otomatik seç
      this.selectedCategory = 0;
      await this.loadCategoryTrainings(0);
    },

    selectCollectionTag(tag) {
      this.selectedCollectionTag = this.selectedCollectionTag === tag ? null : tag;
    },

    async selectCategory(catId) {
      if (this.selectedCategory === catId) return;
      this.selectedCategory = catId;
      await this.loadCategoryTrainings(catId);
    },

    restoreCategorySelection() {
      this.selectedCategory = 0;
      this.loadCategoryTrainings(0);
    },

    async loadCategoryTrainings(categoryId) {
      const dispatches = [];
      if (!this.storeCategoryTrainings[`${categoryId}_can_register`]) {
        dispatches.push(this.$store.dispatch('dashboardAzcon/fetchCategoryTrainings', { categoryId, tab: 'can_register' }));
      }
      if (!this.storeCategoryTrainings[`${categoryId}_registered`]) {
        dispatches.push(this.$store.dispatch('dashboardAzcon/fetchCategoryTrainings', { categoryId, tab: 'registered' }));
      }
      if (dispatches.length) {
        this.categoryLoading = true;
        await Promise.allSettled(dispatches);
        this.categoryLoading = false;
      }
    },

    categoryTrainingRoute(training) {
      const query = { tab: 'can_register_trainings', tabId: 0 };
      if (this.selectedCategory) {
        query.education = String(this.selectedCategory);
      }
      return { name: 'user.education-category', query };
    },

    onTabChange(key) {
      this.activeTab = key;
      if (key === 'integrations' && !this.selectedProvider && this.activeProviders.length) {
        this.selectProvider(this.activeProviders[0].key);
      }
    },

    async selectProvider(providerKey) {
      this.selectedProvider = providerKey;
      if (providerKey === GOFLUENT_PROVIDER.key) return;
      if (!this.providerTrainings[providerKey]) {
        this.providerLoading = true;
        await this.$store.dispatch('dashboardAzcon/fetchProviderTrainings', { provider: providerKey, limit: 6 });
        this.providerLoading = false;
      }
    },
    openInstructor(instructor) {
      this.selectedInstructor = instructor;
      document.body.style.overflow = 'hidden';
    },
    useInstructorFallbackPhoto(event) {
      if (event.target.dataset.fallbackApplied) return;
      event.target.dataset.fallbackApplied = "true";
      event.target.src = noPhoto;
    },
    closeInstructor() {
      this.selectedInstructor = null;
      document.body.style.overflow = '';
    },
    handleInstructorKeydown(event) {
      if (event.key === 'Escape' && this.selectedInstructor) {
        this.closeInstructor();
      }
    },
    assignmentRoute(assignment) {
      return {
        name: 'user.assignment-detail',
        params: { id: assignment.id },
      };
    },
    assignmentDate(assignment) {
      const date = assignment.end_date || assignment.time || assignment.start_date;
      return date
        ? moment(date).format('DD MMMM YYYY')
        : "";
    },
    assignmentStatus(assignment) {
      if (assignment.action_label) {
        return this.$t(`general.${assignment.action_label}`);
      }
      const completion = Number(assignment.completion_percentage || 0);
      if (completion >= 100) return this.$t('general.completed');
      if (completion > 0) return this.$t('general.in_progress');
      return this.$t('general.assigned');
    },
  },
  mounted() {
    this.loadCalendarLayoutPreference();
    this.initializePage();
    document.addEventListener('keydown', this.handleInstructorKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleInstructorKeydown);
    document.body.style.overflow = '';
  },
};
</script>

<style lang="scss" scoped src="./HomePage.scss"></style>
